// Deal detection from the committed price-history snapshots (data/price-history.json).
//
// The weekly PAAPI refresh (scripts/paapi-refresh-prices.ts, run by the
// .github/workflows/paapi-refresh.yml GitHub Action) appends a {date, price} snapshot
// per ASIN and commits it. This module reads that history at build time (no runtime DB,
// no fetch) and turns it into a "deal" signal for product cards.
//
// Until ~3 weekly snapshots accrue for an ASIN, getPriceDeal returns null and no badge
// renders — by design (we don't claim a "deal" without a baseline to compare against).

import historyData from '@/data/price-history.json'

type Snapshot = { d: string; p: number }
interface PriceHistoryFile {
  updatedAt: string | null
  history: Record<string, Snapshot[]>
}

const DATA = historyData as PriceHistoryFile

export type PriceDealKind = 'low' | 'deal' | 'drop'

export interface PriceDeal {
  kind: PriceDealKind
  label: string
  /** Fraction below the 90-day median (0.12 = 12% below), present for 'deal'. */
  pctBelow?: number
}

const WINDOW_DAYS = 90
const MIN_SNAPSHOTS = 3
const DEAL_THRESHOLD = 0.1 // current must be >= 10% below the 90-day median to call it a deal

function daysSince(dateStr: string): number {
  const then = new Date(`${dateStr}T00:00:00Z`).getTime()
  if (Number.isNaN(then)) return Infinity
  return (Date.now() - then) / 86_400_000
}

function median(nums: number[]): number {
  const sorted = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Pull a 10-char ASIN out of an Amazon affiliate URL (e.g. /dp/B0XXXXXXXX). */
export function asinFromUrl(url: string | undefined | null): string | null {
  if (!url) return null
  return url.match(/\/dp\/([A-Z0-9]{10})/)?.[1] ?? null
}

/**
 * Returns a deal signal for an ASIN given its current price, or null if there isn't
 * enough history (or the current price isn't notably good). Priority: lowest > deal > drop.
 */
export function getPriceDeal(
  asin: string | null | undefined,
  currentPrice: number | null | undefined,
): PriceDeal | null {
  if (!asin || currentPrice == null || !Number.isFinite(currentPrice) || currentPrice <= 0) {
    return null
  }
  const snaps = DATA.history[asin]
  if (!snaps || snaps.length < MIN_SNAPSHOTS) return null

  const recent = snaps.filter((s) => daysSince(s.d) <= WINDOW_DAYS)
  if (recent.length < MIN_SNAPSHOTS) return null

  const prices = recent.map((s) => s.p).filter((p) => Number.isFinite(p) && p > 0)
  if (prices.length < MIN_SNAPSHOTS) return null

  const min = Math.min(...prices)
  const med = median(prices)
  // Previous observed price (the snapshot before the latest one).
  const prev = recent.length >= 2 ? recent[recent.length - 2].p : recent[recent.length - 1].p

  if (currentPrice <= min) {
    return { kind: 'low', label: 'Lowest price in 90 days' }
  }
  const pctBelow = med > 0 ? 1 - currentPrice / med : 0
  if (pctBelow >= DEAL_THRESHOLD) {
    return { kind: 'deal', label: `Good deal · ${Math.round(pctBelow * 100)}% below typical`, pctBelow }
  }
  if (currentPrice < prev) {
    return { kind: 'drop', label: 'Price dropped recently' }
  }
  return null
}
